import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectTherapistProfile,
  selectTherapistId,
  selectClinicId,
  setTherapistProfile,
} from "../../store/slices/authSlice";
import {
  shouldPromptForPush,
  dismissPushPrompt,
  requestPushPermissionAndSaveToken,
  refreshPushTokenIfNeeded,
  onForegroundPushMessage,
} from "../../firebase";
import { useToast } from "../../contexts/ToastContext";
import { shouldPromptForPwaInstall } from "../../utils/deviceUtils";
import { BellRinging, X } from "@phosphor-icons/react";
import "./PushNotificationPrompt.css";

/**
 * PushNotificationPrompt — two responsibilities:
 *
 * 1. **Auto-prompt banner**: Shown once after 3+ days since registration,
 *    if the user hasn't enabled push yet. Can be dismissed (re-shown after 30 days).
 *
 * 2. **Silent token refresh + foreground listener**: On every mount,
 *    refreshes the FCM token (if permission is already granted) and
 *    registers a foreground push listener so in-app toasts are shown
 *    when the app is in the foreground.
 */
function PushNotificationPrompt() {
  const dispatch = useDispatch();
  const therapistProfile = useSelector(selectTherapistProfile);
  const therapistId = useSelector(selectTherapistId);
  const clinicId = useSelector(selectClinicId);
  const { showSuccess, showError, showInfo } = useToast();

  const [showBanner, setShowBanner] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  // Decide whether to show the auto-prompt banner.
  // Skipped while the "install CliniKit" banner is eligible (mobile browser tab) —
  // both are fixed at the bottom and install comes first; push is asked inside the PWA.
  useEffect(() => {
    if (
      shouldPromptForPush(therapistProfile) &&
      !shouldPromptForPwaInstall(therapistProfile?.uid)
    ) {
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [therapistProfile]);

  // Silent token refresh + foreground push listener (runs once on mount)
  useEffect(() => {
    if (!therapistId || !clinicId) return;

    // Never re-register a token for a therapist who explicitly disabled push —
    // that silently re-enabled notifications on the next page load.
    if (therapistProfile?.pushNotificationsEnabled !== false) {
      refreshPushTokenIfNeeded(therapistId, clinicId);
    }

    let unsubscribe = null;
    onForegroundPushMessage((payload) => {
      const title = payload.notification?.title || payload.data?.title;
      const body = payload.notification?.body || payload.data?.body;
      if (title) {
        showInfo(`${title}${body ? ` — ${body}` : ""}`);
      }
    }).then((unsub) => {
      unsubscribe = unsub;
    });

    // Handle notification click messages from service worker
    const handleSwMessage = (event) => {
      if (event.data?.type === "PUSH_NOTIFICATION_CLICK" && event.data?.url) {
        window.location.href = event.data.url;
      }
    };
    navigator.serviceWorker?.addEventListener("message", handleSwMessage);

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
      navigator.serviceWorker?.removeEventListener("message", handleSwMessage);
    };
  }, [therapistId, clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnable = useCallback(async () => {
    if (!therapistId || !clinicId) return;

    setIsRequesting(true);
    try {
      const result = await requestPushPermissionAndSaveToken(therapistId, clinicId);

      if (result.success) {
        showSuccess("התראות פוש הופעלו בהצלחה! תקבל התראות גם כשהאפליקציה סגורה.");
        dispatch(
          setTherapistProfile({
            ...therapistProfile,
            pushNotificationsEnabled: true,
          })
        );
        setShowBanner(false);
      } else if (result.permission === "denied") {
        showError("ההרשאה להתראות נחסמה. ניתן לשנות זאת בהגדרות הדפדפן.");
        setShowBanner(false);
      } else {
        showError("לא הצלחנו להפעיל התראות פוש. נסה שוב מאוחר יותר.");
      }
    } catch {
      showError("שגיאה בהפעלת התראות פוש.");
    } finally {
      setIsRequesting(false);
    }
  }, [therapistId, clinicId, therapistProfile, dispatch, showSuccess, showError]);

  const handleDismiss = useCallback(() => {
    if (therapistId) {
      dismissPushPrompt(therapistId);
    }
    setShowBanner(false);
  }, [therapistId]);

  if (!showBanner) return null;

  return (
    <div className="push-prompt-banner">
      <div className="push-prompt-content">
        <div className="push-prompt-icon">
          <BellRinging size={24} weight="duotone" />
        </div>
        <div className="push-prompt-text">
          <strong>הפעל התראות פוש</strong>
          <span>קבל עדכונים חשובים ישירות לנייד — גם כשהאפליקציה סגורה</span>
        </div>
        <div className="push-prompt-actions">
          <button
            className="push-prompt-enable"
            onClick={handleEnable}
            disabled={isRequesting}
          >
            {isRequesting ? "מפעיל..." : "הפעל"}
          </button>
          <button
            className="push-prompt-dismiss"
            onClick={handleDismiss}
            aria-label="סגור"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default PushNotificationPrompt;
